import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { pageContainer, cardStyle } from '../styles/theme';
import Toast from '../components/Toast';
import { useToast } from '../utils/useAdminPage';
import { AdminConversation, AdminMessage, getAdminConversations, getAdminMessages, sendAdminMessage, startAdminChat } from '../services/adminChatService';
import API from '../services/api';
import { getCurrentUserId } from '../utils/currentUser';
import { updateTicketStatus, escalateTicket, transferSupportTicketTeam, getDepartments, getAdmins, AdminListItem } from '../services/adminService';
import VoiceMessagePlayer from '../components/VoiceMessagePlayer';

interface ChatInputProps {
  value: string;
  onChangeVal: React.Dispatch<React.SetStateAction<string>>;
  onSend: (text: string) => void;
  isListening: boolean;
  toggleSpeechRecognition: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ value, onChangeVal, onSend, isListening, toggleSpeechRecognition }) => {
  const [localVal, setLocalVal] = useState(value);

  useEffect(() => {
    setLocalVal(value);
  }, [value]);

  const handleSend = () => {
    if (localVal.trim()) {
      onSend(localVal);
      setLocalVal('');
    }
  };

  return (
    <div style={{ padding: '10px 20px 16px', display: 'flex', gap: 10, background: '#fff', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
      {/* Mic Button */}
      <button
        onClick={toggleSpeechRecognition}
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          border: 'none',
          background: isListening 
            ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
            : '#f1f5f9',
          color: isListening ? '#fff' : '#64748b',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isListening ? '0 0 12px rgba(239, 68, 68, 0.4)' : 'none',
          transition: 'all 0.2s ease',
          animation: isListening ? 'micPulse 1.2s infinite alternate' : 'none',
          flexShrink: 0
        }}
        title={isListening ? 'Listening... Click to stop' : 'Speech to text'}
      >
        <span style={{ fontSize: 18 }}>🎙️</span>
      </button>
      <input
        type="text"
        value={localVal}
        placeholder={isListening ? "Listening..." : "Type a message..."}
        onChange={e => {
          setLocalVal(e.target.value);
          onChangeVal(e.target.value);
        }}
        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
        className="premium-input"
        style={{ flex: 1, padding: '12px 18px', borderRadius: '24px', fontSize: 14, outline: 'none', background: '#f8fafc', color: '#1e293b', border: isListening ? '1.5px solid #ef4444' : '1px solid hsl(var(--border))' }}
      />
      <button
        onClick={handleSend}
        disabled={!localVal.trim()}
        style={{
          background: localVal.trim() ? '#4f46e5' : '#f1f5f9',
          color: localVal.trim() ? '#fff' : '#94a3b8',
          border: 'none',
          borderRadius: '24px',
          padding: '12px 24px',
          fontWeight: 600,
          fontSize: 13,
          cursor: localVal.trim() ? 'pointer' : 'not-allowed',
          transition: 'all 0.15s ease',
          boxShadow: localVal.trim() ? '0 2px 8px rgba(79, 70, 229, 0.25)' : 'none',
          flexShrink: 0
        }}
      >
        Send
      </button>
    </div>
  );
};

const Messages = () => {
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [loadingConv, setLoadingConv] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [inputText, setInputText] = useState('');
  const [search, setSearch] = useState('');
  
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  const adminId = getCurrentUserId();

  const [resolveTicketId, setResolveTicketId] = useState<string | null>(null);
  const [resolutionMessage, setResolutionMessage] = useState('');
  const [resolving, setResolving] = useState(false);

  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  const [rightSidebarTab, setRightSidebarTab] = useState<'notes' | 'activity'>('notes');
  const [activities, setActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const [activeTab, setActiveTab] = useState<'my' | 'unassigned' | 'all'>('my');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [ticketCategories, setTicketCategories] = useState<string[]>(["TECHNICAL", "PAYMENT", "BOOKING", "ACCOUNT", "OTHER"]);

  const [cannedResponses, setCannedResponses] = useState<any[]>([]);
  const [isCannedModalOpen, setIsCannedModalOpen] = useState(false);
  const [newShortcut, setNewShortcut] = useState('');
  const [newText, setNewText] = useState('');
  const [savingCanned, setSavingCanned] = useState(false);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Transfer Team Modal state for Messages page
  const [transferTicketId, setTransferTicketId] = useState<string | null>(null);
  const [targetTeam, setTargetTeam] = useState<string>('OPERATIONS');
  const [transferNote, setTransferNote] = useState<string>('');
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [transferring, setTransferring] = useState<boolean>(false);
  const [departments, setDepartments] = useState<string[]>(['SUPPORT', 'OPERATIONS', 'FINANCE', 'MARKETING']);
  const [assignableAdmins, setAssignableAdmins] = useState<AdminListItem[]>([]);

  useEffect(() => {
    getDepartments().then(depts => {
      if (depts && depts.length > 0) setDepartments(depts);
    }).catch(console.error);

    getAdmins().then(admins => {
      if (admins) setAssignableAdmins(admins.filter(a => a.role !== 'SUPER_ADMIN'));
    }).catch(console.error);
  }, []);

  const fetchActivities = useCallback(async (ticketId: string) => {
    try {
      setLoadingActivities(true);
      const res = await API.get(`/admin/support/${ticketId}/activities`);
      if (res.data.success) {
        setActivities(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch ticket activities', err);
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  const handleEscalateInChat = async (ticketId: string) => {
    try {
      const res = await escalateTicket(ticketId);
      const dispute = res?.data;
      const assignedAdminName = dispute?.assignedAdmin?.name || 'an administrator';
      showToast(`Ticket escalated to dispute and assigned to ${assignedAdminName}`);
      fetchConversations();
      if (ticketId) fetchActivities(ticketId);
    } catch {
      showToast('Failed to escalate ticket', 'error');
    }
  };

  const handleTransferTeamSubmitInChat = async () => {
    if (!transferTicketId) return;
    try {
      setTransferring(true);
      await transferSupportTicketTeam(
        transferTicketId,
        targetTeam,
        transferNote,
        selectedAgentId || undefined
      );
      showToast(`Ticket successfully transferred to ${targetTeam} team!`);
      setTransferTicketId(null);
      setTransferNote('');
      setSelectedAgentId('');
      fetchConversations();
      fetchActivities(transferTicketId);
    } catch (err: any) {
      showToast(err.message || 'Failed to transfer ticket team', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const fetchCannedResponses = useCallback(async () => {
    try {
      const res = await API.get('/admin/canned-responses');
      if (res.data.success) {
        setCannedResponses(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch canned responses', err);
    }
  }, []);

  useEffect(() => {
    fetchCannedResponses();
  }, [fetchCannedResponses]);

  const handleSaveCanned = async () => {
    if (!newShortcut.trim() || !newText.trim()) return;
    try {
      setSavingCanned(true);
      const res = await API.post('/admin/canned-responses', {
        shortcut: newShortcut,
        text: newText
      });
      if (res.data.success) {
        showToast('Canned response saved successfully');
        setNewShortcut('');
        setNewText('');
        fetchCannedResponses();
      }
    } catch {
      showToast('Failed to save canned response', 'error');
    } finally {
      setSavingCanned(false);
    }
  };

  const handleDeleteCanned = async (id: string) => {
    try {
      const res = await API.delete(`/admin/canned-responses/${id}`);
      if (res.data.success) {
        showToast('Canned response deleted');
        fetchCannedResponses();
      }
    } catch {
      showToast('Failed to delete canned response', 'error');
    }
  };

  const toggleSpeechRecognition = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showToast('Speech recognition is not supported in this browser.', 'error');
      return;
    }

    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';

    rec.onstart = () => {
      setIsListening(true);
    };

    rec.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        setInputText((prev) => {
          const space = prev ? (prev.endsWith(' ') ? '' : ' ') : '';
          return prev + space + transcript;
        });
      }
    };

    rec.onerror = (e: any) => {
      console.error('Speech recognition error', e);
      setIsListening(false);
      showToast('Speech recognition failed or was interrupted.', 'error');
    };

    rec.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = rec;
    rec.start();
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { getConfig } = await import('../services/adminService');
        const configs = await getConfig('general');
        const categoriesCfg = configs.find(c => c.key === 'SUPPORT_TICKET_CATEGORIES');
        if (categoriesCfg) {
          setTicketCategories(JSON.parse(categoriesCfg.value));
        }
      } catch (e) {
        console.error('Failed to load categories', e);
      }
    };
    loadCategories();
  }, []);

  const handleAddNote = async () => {
    if (!noteText.trim() || !activeConv || !activeConv.supportTicketId) return;
    try {
      setAddingNote(true);
      const res = await API.post(`/admin/support/${activeConv.supportTicketId}/notes`, { content: noteText });
      if (res.data.success) {
        showToast('Private note added successfully');
        setNoteText('');
        fetchConversations();
        fetchActivities(activeConv.supportTicketId);
      }
    } catch {
      showToast('Failed to add private note', 'error');
    } finally {
      setAddingNote(false);
    }
  };
  
  const { toast, showToast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastRoomIdRef = useRef<string | null>(null);

  const [isFocusMode, setIsFocusMode] = useState(false);
  const [showLeftDrawer, setShowLeftDrawer] = useState(false);
  const [showRightDrawer, setShowRightDrawer] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { roomId: urlRoomId } = useParams<{ roomId: string }>();

  // Sync active room from URL param (param may be full ticketId, 8-char short ticketId, or roomId)
  useEffect(() => {
    if (!urlRoomId || conversations.length === 0) return;
    const cleanId = urlRoomId.replace('#', '').trim();
    const conv = conversations.find(
      c => c.supportTicketId === cleanId ||
           (c.supportTicketId && c.supportTicketId.slice(0, 8) === cleanId) ||
           c.id === cleanId ||
           (c.id && c.id.slice(0, 8) === cleanId)
    );
    if (conv) {
      setActiveRoomId(conv.id); // always use actual roomId for API calls
    }
  }, [urlRoomId, conversations]);

  // Backwards-compat: support navigation via location.state
  useEffect(() => {
    if (location.state?.activeRoomId) {
      const conv = conversations.find(c => c.id === location.state.activeRoomId);
      const urlId = conv?.supportTicketId ? conv.supportTicketId.slice(0, 8) : location.state.activeRoomId;
      navigate(`/messages/${urlId}`, { replace: true, state: null });
    }
  }, [location.state, conversations, navigate]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoadingConv(true);
      const data = await getAdminConversations();
      setConversations(data);
    } catch (e) {
      showToast('Failed to load conversations', 'error');
    } finally {
      setLoadingConv(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const errorCountRef = useRef(0);

  const fetchMessages = useCallback(async (roomId: string) => {
    try {
      setLoadingMsg(true);
      const data = await getAdminMessages(roomId);
      setMessages(data);
      errorCountRef.current = 0; // Reset on success
    } catch (e) {
      if (errorCountRef.current === 0) {
        showToast('Failed to fetch messages', 'error');
      }
      errorCountRef.current += 1;
    } finally {
      setLoadingMsg(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!activeRoomId) {
      lastRoomIdRef.current = null;
      return;
    }

    const container = chatContainerRef.current;
    if (!container) return;

    const isNewRoom = lastRoomIdRef.current !== activeRoomId;
    if (isNewRoom) {
      lastRoomIdRef.current = activeRoomId;
      // Scroll to bottom immediately
      container.scrollTop = container.scrollHeight;
      return;
    }

    // Check if the user was scrolled near the bottom before the new message rendered
    const { scrollTop, scrollHeight, clientHeight } = container;
    // We check if the scroll position was close to the bottom (within 150px)
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;

    if (isNearBottom) {
      setTimeout(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  }, [messages, activeRoomId]);

  useEffect(() => {
    if (activeRoomId) {
      errorCountRef.current = 0; // Reset error count when room changes
      fetchMessages(activeRoomId);
      const interval = setInterval(() => fetchMessages(activeRoomId), 5000);
      return () => clearInterval(interval);
    }
  }, [activeRoomId, fetchMessages]);


  const handleStartDirectChat = async (userId: string) => {
    try {
      const room = await startAdminChat({ targetUserId: userId });
      await fetchConversations();
      setActiveRoomId(room.id);
      setUserSearchQuery('');
      setUserSearchResults([]);
      showToast('Chat started');
    } catch {
      showToast('Failed to start chat', 'error');
    }
  };

  // User search effect
  useEffect(() => {
    if (userSearchQuery.trim().length < 2) {
      setUserSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const { getUsers } = await import('../services/adminService');
        const res = await getUsers({ search: userSearchQuery });
        setUserSearchResults(res.items.slice(0, 5));
      } catch (e) {
        console.error('User search failed', e);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  const activeConv = conversations.find(c => c.id === activeRoomId);

  useEffect(() => {
    if (activeConv?.supportTicketId) {
      fetchActivities(activeConv.supportTicketId);
    }
  }, [activeConv?.supportTicketId, fetchActivities]);
  const filteredConvs = conversations.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.subtitle.toLowerCase().includes(search.toLowerCase());
    
    if (!matchesSearch) return false;

    if (activeTab === 'my') {
      const isAssignedToMe = c.supportTicket?.assignedTo === adminId;
      const isDirect = c.type === 'DIRECT';
      const isBooking = c.type === 'BOOKING' && c.participantIds.includes(adminId || '');
      return isAssignedToMe || isDirect || isBooking;
    } else if (activeTab === 'unassigned') {
      const isUnassigned = !c.supportTicket?.assignedTo;
      const isUnresolved = c.supportTicket?.status !== 'RESOLVED' && c.supportTicket?.status !== 'CLOSED';
      return c.type === 'SUPPORT' && isUnassigned && isUnresolved;
    }
    
    return true;
  });

  const leftSidebarStyle: React.CSSProperties = isFocusMode
    ? {
        position: 'absolute',
        left: showLeftDrawer ? 0 : -340,
        top: 0,
        bottom: 0,
        width: 340,
        minWidth: 340,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
        background: 'hsl(var(--card))',
        boxShadow: '4px 0 16px rgba(0, 0, 0, 0.15)',
        borderRight: '1px solid hsl(var(--border))',
        transition: 'left 0.25s ease-out',
      }
    : {
        ...cardStyle,
        width: isSidebarCollapsed ? '0px' : '340px',
        minWidth: isSidebarCollapsed ? '0px' : '340px',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
        borderRight: isSidebarCollapsed ? 'none' : '1px solid hsl(var(--border))',
        background: 'hsl(var(--card))',
        transition: 'width 0.2s ease, min-width 0.2s ease',
      };

  const showRightPanel = activeRoomId && activeConv?.type === 'SUPPORT';
  const rightPanelStyle: React.CSSProperties = isFocusMode
    ? {
        position: 'absolute',
        right: showRightDrawer ? 0 : -300,
        top: 0,
        bottom: 0,
        width: 300,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        overflowY: 'auto',
        background: '#ffffff',
        boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.15)',
        borderLeft: '1px solid #f1f5f9',
        transition: 'right 0.25s ease-out',
      }
    : {
        width: '300px',
        borderLeft: '1px solid #f1f5f9',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        overflowY: 'auto',
        background: '#ffffff',
        flexShrink: 0
      };

  return (
    <div style={{
      ...pageContainer,
      display: 'flex',
      flexDirection: 'column',
      height: isFocusMode ? '100vh' : 'calc(100vh - 90px)',
      padding: isFocusMode ? 0 : pageContainer.padding || '32px',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {isFocusMode && (
        <style>{`
          aside, nav {
            display: none !important;
          }
          .app-shell-panel main {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
          }
        `}</style>
      )}
      <style>{`
        .chat-scroll::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .chat-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .chat-scroll::-webkit-scrollbar-thumb {
          background: hsl(var(--border));
          border-radius: 99px;
        }
        .chat-scroll::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
        
        .premium-input {
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
        }
        .premium-input:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        
        .conv-item {
          transition: all 0.15s ease;
        }
        .conv-item:hover {
          background: hsl(var(--muted)/0.4);
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .message-bubble {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
      
      <Toast toast={toast} />

      <div style={{ display: 'flex', flex: 1, gap: '20px', minHeight: 0, overflow: 'hidden' }}>
        {/* SIDEBAR */}
        <div style={leftSidebarStyle}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid hsl(var(--border))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'hsl(var(--foreground))' }}>Conversations</h3>
                <span style={{ fontSize: 11, background: 'hsl(var(--primary)/0.1)', color: 'hsl(var(--primary))', padding: '2px 8px', borderRadius: 99, fontWeight: 700 }}>
                  {filteredConvs.length}
                </span>
              </div>
              {isFocusMode ? (
                <button
                  onClick={() => setShowLeftDrawer(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#64748b', fontWeight: 'bold' }}
                >
                  ✕
                </button>
              ) : (
                <button
                  onClick={() => setIsSidebarCollapsed(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}
                  title="Collapse Sidebar"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
              )}
            </div>

            {/* Sub-Navigation Filtering Tabs */}
            <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', padding: 3, borderRadius: 8, marginBottom: 12 }}>
              <button
                onClick={() => setActiveTab('my')}
                style={{
                  flex: 1,
                  background: activeTab === 'my' ? '#fff' : 'transparent',
                  color: activeTab === 'my' ? 'hsl(var(--foreground))' : '#64748b',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 0',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: activeTab === 'my' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'
                }}
              >
                My Chats
              </button>
              <button
                onClick={() => setActiveTab('unassigned')}
                style={{
                  flex: 1,
                  background: activeTab === 'unassigned' ? '#fff' : 'transparent',
                  color: activeTab === 'unassigned' ? 'hsl(var(--foreground))' : '#64748b',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 0',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: activeTab === 'unassigned' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'
                }}
              >
                Unassigned
              </button>
              <button
                onClick={() => setActiveTab('all')}
                style={{
                  flex: 1,
                  background: activeTab === 'all' ? '#fff' : 'transparent',
                  color: activeTab === 'all' ? 'hsl(var(--foreground))' : '#64748b',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 0',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: activeTab === 'all' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'
                }}
              >
                All
              </button>
            </div>

            <input
              type="text"
              placeholder="Search conversations..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="premium-input"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8, padding: '8px 12px', borderRadius: '8px', fontSize: 12, outline: 'none', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))' }}
            />
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search user by name/email..."
                value={userSearchQuery}
                onChange={e => setUserSearchQuery(e.target.value)}
                className="premium-input"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: '8px', fontSize: 12, outline: 'none', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))' }}
              />
              
              {userSearchResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
                  borderRadius: 10, marginTop: 4, zIndex: 10,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)', overflow: 'hidden'
                }}>
                  {userSearchResults.map(u => (
                    <div
                      key={u.id}
                      onClick={() => handleStartDirectChat(u.id)}
                      style={{
                        padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid hsl(var(--border)/0.5)',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--muted))'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'hsl(var(--foreground))' }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                    </div>
                  ))}
                </div>
              )}

              {isSearchingUsers && (
                <div style={{ position: 'absolute', right: 12, top: 10, fontSize: 10, color: '#94a3b8' }}>
                  Searching...
                </div>
              )}
            </div>
          </div>

          <div className="chat-scroll" style={{ flex: 1, overflowY: 'auto' }}>
            {loadingConv && <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>Loading conversations...</div>}
            {!loadingConv && filteredConvs.length === 0 && (
              <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>
                No active conversations.
              </div>
            )}
            {filteredConvs.map(c => {
              const isResolved = c.type === 'SUPPORT' && 
                (c.supportTicket?.status === 'RESOLVED' || c.supportTicket?.status === 'CLOSED');

              return (
                <div
                  key={c.id}
                  onClick={() => {
                    const rawId = c.type === 'SUPPORT' && c.supportTicketId ? c.supportTicketId : c.id;
                    const urlId = rawId.length > 8 ? rawId.slice(0, 8) : rawId;
                    navigate(`/messages/${urlId}`);
                  }}
                  className="conv-item"
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid hsl(var(--border)/0.4)',
                    cursor: 'pointer',
                    background: activeRoomId === c.id ? 'hsl(var(--primary)/0.04)' : 'transparent',
                    borderLeft: activeRoomId === c.id ? '4px solid hsl(var(--primary))' : '4px solid transparent',
                    opacity: isResolved ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: activeRoomId === c.id ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textDecoration: isResolved ? 'line-through' : 'none'
                    }}>
                      {c.title}
                    </div>
                    <span style={{
                      fontSize: 9,
                      color: isResolved 
                        ? '#4b5563' 
                        : c.type === 'SUPPORT' 
                          ? '#4f46e5' 
                          : c.type === 'BOOKING' 
                            ? '#059669' 
                            : '#4b5563',
                      background: isResolved 
                        ? '#f3f4f6' 
                        : c.type === 'SUPPORT' 
                          ? '#e0e7ff' 
                          : c.type === 'BOOKING' 
                            ? '#d1fae5' 
                            : '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      marginLeft: 8,
                      flexShrink: 0
                    }}>
                      {isResolved ? c.supportTicket?.status : c.type}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.subtitle}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontStyle: c.lastMessage ? 'normal' : 'italic' }}>
                    {c.lastMessage || 'Start of conversation'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CHAT AREA & RIGHT SIDEBAR PANEL */}
        <div style={{ flex: 1, display: 'flex', minWidth: 0, overflow: 'hidden', border: '1px solid hsl(var(--border))', borderRadius: 12, background: 'hsl(var(--card))', position: 'relative' }}>
          {isSidebarCollapsed && (
            <button
              onClick={() => setIsSidebarCollapsed(false)}
              style={{
                position: 'absolute',
                left: 10,
                top: 15,
                zIndex: 10,
                width: 32,
                height: 32,
                background: '#fff',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                color: 'hsl(var(--primary))'
              }}
              title="Expand Sidebar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          )}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            {activeRoomId ? (
              <>
                {/* Chat Header */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'hsl(var(--foreground))' }}>{activeConv?.title}</h3>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{activeConv?.subtitle}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                      onClick={() => {
                        setIsFocusMode(!isFocusMode);
                        setShowLeftDrawer(false);
                        setShowRightDrawer(false);
                      }}
                      style={{
                        background: isFocusMode ? 'hsl(var(--primary)/0.12)' : '#f8fafc',
                        border: isFocusMode ? '1px solid hsl(var(--primary)/0.3)' : '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '7px 14px',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: isFocusMode ? 'hsl(var(--primary))' : '#64748b',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.2s'
                      }}
                      title="Toggle distraction-free Focus Mode"
                    >
                      <span>{isFocusMode ? '🗗 Exit Focus' : '⛶ Focus Mode'}</span>
                    </button>
                    {activeConv?.type === 'SUPPORT' && activeConv?.supportTicketId && (
                      <>
                        <button
                          onClick={() => {
                            setTransferTicketId(activeConv.supportTicketId!);
                            setTargetTeam('OPERATIONS');
                            setTransferNote('');
                            setSelectedAgentId('');
                          }}
                          style={{
                            border: '1px solid #fed7aa',
                            background: '#fff7ed',
                            color: '#c2410c',
                            fontWeight: 700,
                            fontSize: 12,
                            padding: '7px 12px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                          title="Move ticket to Operations, Tech, or Finance team"
                        >
                          <span>🔄</span> Move Team
                        </button>

                        {activeConv.supportTicket?.status !== 'RESOLVED' && activeConv.supportTicket?.status !== 'CLOSED' && (
                          <button
                            onClick={() => handleEscalateInChat(activeConv.supportTicketId!)}
                            style={{
                              border: '1px solid #fecaca',
                              background: '#fef2f2',
                              color: '#b91c1c',
                              fontWeight: 700,
                              fontSize: 12,
                              padding: '7px 12px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                            title="Escalate ticket to dispute"
                          >
                            <span>⚡</span> Escalate
                          </button>
                        )}
                      </>
                    )}
                    {activeConv?.type === 'SUPPORT' && 
                      activeConv?.supportTicket?.status !== 'RESOLVED' && 
                      activeConv?.supportTicket?.status !== 'CLOSED' && (
                        <button
                          onClick={() => setResolveTicketId(activeConv.supportTicketId || null)}
                          style={{ background: '#e6fcf5', color: '#0ca678', border: '1px solid rgba(12, 166, 120, 0.25)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'all 0.15s ease' }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = '#0ca678';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = '#e6fcf5';
                            e.currentTarget.style.color = '#0ca678';
                          }}
                        >
                          ✓ Resolve Chat
                        </button>
                    )}
                    <button
                      onClick={() => fetchMessages(activeRoomId)}
                      style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: '#64748b', fontWeight: 600 }}
                    >
                      ↻ Refresh
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div ref={chatContainerRef} className="chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {loadingMsg && messages.length === 0 && (
                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: 20, fontSize: 13 }}>Loading messages...</div>
                  )}

                  {messages.length === 0 && !loadingMsg && (
                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40, fontSize: 13 }}>
                      No messages yet. Start the conversation!
                    </div>
                  )}

                  {messages.map((m) => {
                    const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "AD_MANAGER", "OPERATIONS"];
                    const isMe = adminId ? m.senderId === adminId : false;
                    const isOtherAdmin = !isMe && m.sender?.role && ADMIN_ROLES.includes(m.sender.role);

                    // Setup label
                    let senderLabel = 'User';
                    if (isMe) {
                      const roleLabel = m.sender?.role ? m.sender.role.replace('_', ' ') : 'Admin';
                      senderLabel = `You (${roleLabel})`;
                    } else if (isOtherAdmin) {
                      const roleLabel = m.sender?.role ? m.sender.role.replace('_', ' ') : 'Admin';
                      senderLabel = `${m.sender?.name || 'Staff'} (${roleLabel})`;
                    } else {
                      const roleLabel = m.sender?.role === 'SCHOLAR' ? 'Scholar' : 'Customer';
                      senderLabel = `${m.sender?.name || 'User'} (${roleLabel})`;
                    }

                    const alignEnd = Boolean(isMe || isOtherAdmin);
                    const bubbleBg = isMe 
                      ? 'linear-gradient(135deg, #6366f1, #4f46e5)' 
                      : isOtherAdmin 
                        ? 'linear-gradient(135deg, #475569, #334155)' 
                        : '#ffffff';
                    const bubbleColor = alignEnd ? '#fff' : '#1e293b';
                    const bubbleBorder = alignEnd ? 'none' : '1px solid #e2e8f0';
                    const bubbleRadius = alignEnd ? '12px 12px 2px 12px' : '12px 12px 12px 2px';
                    const bubbleShadow = alignEnd ? '0 2px 8px rgba(79, 70, 229, 0.15)' : '0 1px 3px rgba(0,0,0,0.02)';

                    const isVoice = (m.content && m.content.includes('[Voice Message]')) || 
                                    (m.mediaUrl && /\.(m4a|mp3|wav|aac|ogg|opus|webm|mp4)(\?.*)?$/i.test(m.mediaUrl)) || 
                                    (m.mediaUrl && (m.mediaUrl.includes('/audio/') || m.mediaUrl.includes('recording')));
                    const isImage = m.mediaUrl && /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(m.mediaUrl);

                    return (
                      <div key={m.id} className="message-bubble" style={{ display: 'flex', flexDirection: 'column', alignItems: alignEnd ? 'flex-end' : 'flex-start', maxWidth: '80%', alignSelf: alignEnd ? 'flex-end' : 'flex-start' }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, fontWeight: 500, paddingLeft: alignEnd ? 0 : 4, paddingRight: alignEnd ? 4 : 0 }}>
                          {senderLabel} · {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>

                        {/* If it's an audio voice message with a mediaUrl */}
                        {isVoice && m.mediaUrl ? (
                          <VoiceMessagePlayer audioUrl={m.mediaUrl} isSender={alignEnd} />
                        ) : (
                          <>
                            {/* Standard message text bubble */}
                            {m.content && (
                              <div style={{
                                background: bubbleBg,
                                padding: '12px 16px',
                                borderRadius: bubbleRadius,
                                boxShadow: bubbleShadow,
                                border: bubbleBorder,
                                color: bubbleColor,
                                fontSize: 14,
                                lineHeight: 1.5,
                                wordBreak: 'break-word',
                              }}>
                                {m.content}
                              </div>
                            )}

                            {/* Image attachment */}
                            {m.mediaUrl && isImage && (
                              <img
                                src={m.mediaUrl}
                                alt="Media"
                                style={{ maxWidth: 220, maxHeight: 200, objectFit: 'cover', borderRadius: 10, marginTop: 6, cursor: 'pointer', border: '1px solid #e2e8f0', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                                onClick={() => window.open(m.mediaUrl!, '_blank')}
                                onError={e => (e.currentTarget.style.display = 'none')}
                              />
                            )}

                            {/* Generic file attachment fallback */}
                            {m.mediaUrl && !isImage && !isVoice && (
                              <a
                                href={m.mediaUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  marginTop: 6,
                                  padding: '8px 12px',
                                  borderRadius: 8,
                                  background: '#f1f5f9',
                                  color: '#2563eb',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textDecoration: 'none',
                                  border: '1px solid #cbd5e1'
                                }}
                              >
                                📎 View Attached File ↗
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Canned Responses Row */}
                <div style={{ padding: '8px 20px 0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: '#fff', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginRight: 4 }}>Quick Replies:</span>
                  
                  {cannedResponses.slice(0, 3).map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setInputText(r.text)}
                      style={{
                        padding: '4px 10px',
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: 16,
                        fontSize: 11,
                        color: '#475569',
                        cursor: 'pointer',
                        fontWeight: 500,
                        transition: 'all 0.15s'
                      }}
                      title={r.text}
                    >
                      {r.shortcut}
                    </button>
                  ))}

                  {cannedResponses.length > 3 && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          setInputText(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: 16,
                        fontSize: 11,
                        color: '#475569',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value="">More...</option>
                      {cannedResponses.slice(3).map((r) => (
                        <option key={r.id} value={r.text}>{r.shortcut}</option>
                      ))}
                    </select>
                  )}

                  <button
                    onClick={() => setIsCannedModalOpen(true)}
                    style={{
                      padding: '4px 8px',
                      background: 'transparent',
                      border: 'none',
                      fontSize: 11,
                      color: '#4f46e5',
                      cursor: 'pointer',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3
                    }}
                  >
                    ⚙️ Manage
                  </button>
                </div>

                {/* Input Subcomponent */}
                <ChatInput
                  value={inputText}
                  onChangeVal={setInputText}
                  onSend={async (text) => {
                    if (!activeRoomId) return;
                    try {
                      setInputText('');
                      const newMessage = await sendAdminMessage(activeRoomId, text);
                      setMessages((prev) => [...prev, newMessage]);
                      setTimeout(() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    } catch {
                      showToast('Failed to send message', 'error');
                    }
                  }}
                  isListening={isListening}
                  toggleSpeechRecognition={toggleSpeechRecognition}
                />
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexDirection: 'column', background: '#f8fafc' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>💬</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#475569' }}>No Chat Selected</div>
                <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', maxWidth: 280, margin: '6px 0 0 0', lineHeight: 1.4 }}>
                  Select a conversation from the left pane, or search for an active user to initiate a direct message.
                </p>
              </div>
            )}
          </div>

          {/* RIGHT SIDE PANEL FOR SUPPORT TICKETS */}
          {showRightPanel && (
            <div style={rightPanelStyle} className="chat-scroll">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px 0' }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ticket Details</h4>
                {isFocusMode && (
                  <button
                    onClick={() => setShowRightDrawer(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#64748b', fontWeight: 'bold' }}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, marginBottom: 24, borderBottom: '1px solid #f1f5f9', paddingBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>Status</span>
                  <span style={{
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    color: activeConv.supportTicket?.status === 'RESOLVED' ? '#0f766e' : '#b45309',
                    background: activeConv.supportTicket?.status === 'RESOLVED' ? '#ccfbf1' : '#fef3c7',
                    padding: '2px 8px',
                    borderRadius: 6
                  }}>{activeConv.supportTicket?.status}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>Priority</span>
                  <span style={{
                    fontWeight: 700,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    color: activeConv.supportTicket?.priority === 'HIGH' ? '#be123c' : '#4b5563',
                    background: activeConv.supportTicket?.priority === 'HIGH' ? '#ffe4e6' : '#f3f4f6',
                    padding: '2px 8px',
                    borderRadius: 6
                  }}>{activeConv.supportTicket?.priority}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: '#64748b', fontWeight: 500, flexShrink: 0 }}>Category</span>
                  {activeConv.supportTicket?.status === 'RESOLVED' || activeConv.supportTicket?.status === 'CLOSED' ? (
                    <span style={{ fontWeight: 600, color: '#334155' }}>{activeConv.supportTicket?.category || 'OTHER'}</span>
                  ) : (
                    <select
                      value={activeConv.supportTicket?.category || 'OTHER'}
                      onChange={async (e) => {
                        const newCat = e.target.value;
                        try {
                          await API.patch(`/admin/support/${activeConv.supportTicketId}/category`, { category: newCat });
                          showToast('Category updated successfully');
                          fetchConversations();
                          if (activeConv?.supportTicketId) {
                            fetchActivities(activeConv.supportTicketId);
                          }
                        } catch {
                          showToast('Failed to update category', 'error');
                        }
                      }}
                      className="premium-input"
                      style={{ padding: '4px 8px', borderRadius: 6, fontSize: 12, outline: 'none', background: '#fff', color: '#1e293b', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                    >
                      {ticketCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>Created</span>
                  <span style={{ color: '#475569' }}>{new Date(activeConv.supportTicket?.createdAt).toLocaleDateString()}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span style={{ color: '#64748b', fontWeight: 500 }}>Assignee</span>
                  <span style={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: activeConv.supportTicket?.assignedAdmin?.name ? '#4f46e5' : '#dc2626',
                    background: activeConv.supportTicket?.assignedAdmin?.name ? '#e0e7ff' : '#fee2e2',
                    padding: '3px 8px',
                    borderRadius: 12
                  }}>
                    {activeConv.supportTicket?.assignedAdmin?.name || 'Unassigned'}
                  </span>
                </div>

                {activeConv?.type === 'SUPPORT' && activeConv?.supportTicketId && (
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button
                      onClick={() => {
                        setTransferTicketId(activeConv.supportTicketId!);
                        setTargetTeam('OPERATIONS');
                        setTransferNote('');
                        setSelectedAgentId('');
                      }}
                      style={{
                        width: '100%',
                        border: '1px solid #fed7aa',
                        background: '#fff7ed',
                        color: '#c2410c',
                        fontWeight: 700,
                        fontSize: 12,
                        padding: '9px 14px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                    >
                      🔄 Move to Different Team
                    </button>

                    {activeConv.supportTicket?.status !== 'RESOLVED' && activeConv.supportTicket?.status !== 'CLOSED' && (
                      <button
                        onClick={() => handleEscalateInChat(activeConv.supportTicketId!)}
                        style={{
                          width: '100%',
                          border: '1px solid #fecaca',
                          background: '#fef2f2',
                          color: '#b91c1c',
                          fontWeight: 700,
                          fontSize: 12,
                          padding: '9px 14px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6
                        }}
                      >
                        ⚡ Escalate to Dispute
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar Tab Switcher */}
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '10px', padding: '3px', marginBottom: '16px' }}>
                <button
                  onClick={() => setRightSidebarTab('notes')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: rightSidebarTab === 'notes' ? '#fff' : 'transparent',
                    color: rightSidebarTab === 'notes' ? '#1e293b' : '#64748b',
                    boxShadow: rightSidebarTab === 'notes' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  📝 Private Notes
                </button>
                <button
                  onClick={() => setRightSidebarTab('activity')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: rightSidebarTab === 'activity' ? '#fff' : 'transparent',
                    color: rightSidebarTab === 'activity' ? '#1e293b' : '#64748b',
                    boxShadow: rightSidebarTab === 'activity' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  ⚡ Chat Activity
                </button>
              </div>

              {/* Tab Content */}
              {rightSidebarTab === 'notes' ? (
                <>
                  {/* Notes list */}
                  <div className="chat-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, minHeight: '180px' }}>
                    {(!activeConv.supportTicket?.privateNotes || activeConv.supportTicket.privateNotes.length === 0) ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '12px 8px', textAlign: 'center' }}>No internal notes.</div>
                    ) : (
                      (activeConv.supportTicket.privateNotes as any[]).map((note) => (
                        <div key={note.id} style={{ background: '#fffbeb', padding: 12, borderRadius: 10, fontSize: 12, border: '1px solid #fef3c7', boxShadow: '0 1px 3px rgba(0,0,0,0.01)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#92400e', fontSize: 9, marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                            <span>{note.createdBy}</span>
                            <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                          </div>
                          <div style={{ color: '#78350f', whiteSpace: 'pre-wrap', lineHeight: 1.4, fontSize: 12 }}>{note.content}</div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Note form */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                    <textarea
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Add private note for representatives..."
                      className="premium-input"
                      style={{ width: '100%', boxSizing: 'border-box', minHeight: 65, padding: '10px', borderRadius: 8, fontSize: 12, outline: 'none', background: '#f8fafc', color: '#1e293b', resize: 'none', lineHeight: 1.4 }}
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={!noteText.trim() || addingNote}
                      style={{
                        background: noteText.trim() ? '#4f46e5' : '#f1f5f9',
                        color: noteText.trim() ? '#fff' : '#94a3b8',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: noteText.trim() ? 'pointer' : 'not-allowed',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {addingNote ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </>
              ) : (
                /* Chat Activity Tab */
                <div className="chat-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {loadingActivities && activities.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>Loading activity logs...</div>
                  ) : activities.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>No activity logged yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'relative', paddingLeft: '14px', borderLeft: '2px solid #e2e8f0', marginLeft: '6px' }}>
                      {activities.map((act) => {
                        const dateStr = new Date(act.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        const dateDayStr = new Date(act.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' });
                        
                        let badgeBg = '#f1f5f9';
                        let badgeColor = '#475569';
                        
                        if (act.action === 'TICKET_CREATED') {
                          badgeBg = '#e0f2fe';
                          badgeColor = '#0369a1';
                        } else if (act.action === 'STATUS_CHANGED') {
                          badgeBg = '#ccfbf1';
                          badgeColor = '#0f766e';
                        } else if (act.action === 'ASSIGNED' || act.action === 'REASSIGNED') {
                          badgeBg = '#e0e7ff';
                          badgeColor = '#4338ca';
                        } else if (act.action === 'PRIVATE_NOTE_ADDED') {
                          badgeBg = '#fef3c7';
                          badgeColor = '#b45309';
                        } else if (act.action === 'ESCALATED') {
                          badgeBg = '#ffe4e6';
                          badgeColor = '#be123c';
                        } else if (act.action === 'CATEGORY_CHANGED') {
                          badgeBg = '#faf5ff';
                          badgeColor = '#6b21a8';
                        }

                        return (
                          <div key={act.id} style={{ position: 'relative' }}>
                            {/* Dot on line */}
                            <div style={{
                              position: 'absolute',
                              left: '-20px',
                              top: '4px',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              background: badgeColor,
                              border: '2px solid #fff',
                              boxShadow: '0 0 0 1px #e2e8f0'
                            }} />

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                fontSize: '9px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                color: badgeColor,
                                background: badgeBg,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                {act.action.replace(/_/g, ' ')}
                              </span>
                              <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                {dateDayStr} · {dateStr}
                              </span>
                            </div>
                            
                            <div style={{ fontSize: '11px', color: '#334155', marginTop: '4px', fontWeight: 500, lineHeight: 1.4 }}>
                              {act.details}
                            </div>
                            
                            {act.actor && (
                              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', fontStyle: 'italic' }}>
                                by {act.actor.name} ({act.actor.role.replace(/_/g, ' ')})
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drawer backdrop overlay */}
        {isFocusMode && (showLeftDrawer || showRightDrawer) && (
          <div
            onClick={() => {
              setShowLeftDrawer(false);
              setShowRightDrawer(false);
            }}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.25)',
              zIndex: 95,
              backdropFilter: 'blur(1.5px)',
              transition: 'opacity 0.2s ease-in-out'
            }}
          />
        )}

        {/* Left Strip Handle */}
        {isFocusMode && !showLeftDrawer && (
          <div
            onClick={() => setShowLeftDrawer(true)}
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '28px',
              height: '140px',
              background: 'hsl(var(--primary))',
              color: '#ffffff',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 90,
              boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
              transition: 'all 0.2s',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.05em'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.width = '32px';
              e.currentTarget.style.background = 'hsl(var(--ring))';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.width = '28px';
              e.currentTarget.style.background = 'hsl(var(--primary))';
            }}
          >
            💬 CONVERSATIONS
          </div>
        )}

        {/* Right Strip Handle */}
        {isFocusMode && showRightPanel && !showRightDrawer && (
          <div
            onClick={() => setShowRightDrawer(true)}
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '28px',
              height: '140px',
              background: 'hsl(var(--primary))',
              color: '#ffffff',
              borderTopLeftRadius: '8px',
              borderBottomLeftRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 90,
              boxShadow: '-2px 0 8px rgba(0,0,0,0.15)',
              transition: 'all 0.2s',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.05em'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.width = '32px';
              e.currentTarget.style.background = 'hsl(var(--ring))';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.width = '28px';
              e.currentTarget.style.background = 'hsl(var(--primary))';
            }}
          >
            📋 TICKET DETAILS
          </div>
        )}
      </div>

      {resolveTicketId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...cardStyle, width: 440, maxWidth: '90%', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', background: '#fff' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Resolve Ticket</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#64748b' }}>Please supply a resolution summary before closing the query.</p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 8, fontWeight: 600, color: '#475569' }}>Resolution Summary</label>
              <textarea value={resolutionMessage} onChange={e => setResolutionMessage(e.target.value)} placeholder="Type what was resolved..." className="premium-input" style={{ width: '100%', boxSizing: 'border-box', minHeight: 110, padding: 12, borderRadius: 8, outline: 'none', resize: 'none', fontSize: 13, lineHeight: 1.4 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setResolveTicketId(null); setResolutionMessage(''); }} style={{ border: 'none', background: '#f1f5f9', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 13 }}>Cancel</button>
              <button
                disabled={resolving}
                onClick={async () => {
                  if (!resolutionMessage.trim()) return showToast('Resolution summary is required', 'error');
                  try {
                    setResolving(true);
                    await updateTicketStatus(resolveTicketId, 'RESOLVED', resolutionMessage, adminId || undefined);
                    showToast('Ticket resolved and closed successfully');
                    setResolveTicketId(null);
                    setResolutionMessage('');
                    fetchConversations();
                    if (activeConv?.supportTicketId) {
                      fetchActivities(activeConv.supportTicketId);
                    }
                  } catch {
                    showToast('Failed to resolve ticket', 'error');
                  } finally {
                    setResolving(false);
                  }
                }}
                style={{ border: 'none', background: '#10b981', color: '#fff', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                {resolving ? 'Resolving...' : 'Confirm Resolve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCannedModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...cardStyle, width: 550, maxWidth: '95%', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Manage Canned Responses</h3>
              <button onClick={() => setIsCannedModalOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            
            {/* Create form */}
            <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase' }}>Add Canned Response</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder="Shortcut (e.g., /greet)"
                  value={newShortcut}
                  onChange={e => setNewShortcut(e.target.value)}
                  className="premium-input"
                  style={{ flex: 1, padding: 8, fontSize: 13 }}
                />
                <button
                  onClick={handleSaveCanned}
                  disabled={savingCanned || !newShortcut.trim() || !newText.trim()}
                  style={{
                    background: '#4f46e5',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  Save
                </button>
              </div>
              <textarea
                placeholder="Response content text..."
                value={newText}
                onChange={e => setNewText(e.target.value)}
                className="premium-input"
                style={{ width: '100%', boxSizing: 'border-box', minHeight: 60, padding: 8, borderRadius: 8, outline: 'none', resize: 'none', fontSize: 13 }}
              />
            </div>

            {/* List */}
            <div className="chat-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 180 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', marginBottom: 4 }}>Saved Templates ({cannedResponses.length})</div>
              {cannedResponses.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 20, fontStyle: 'italic' }}>
                  No canned responses created yet.
                </div>
              ) : (
                cannedResponses.map((r) => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#4f46e5' }}>{r.shortcut}</div>
                      <div style={{ fontSize: 12, color: '#334155', marginTop: 4, whiteSpace: 'pre-wrap' }}>{r.text}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteCanned(r.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                        padding: '2px 6px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setIsCannedModalOpen(false)} style={{ border: 'none', background: '#f1f5f9', padding: '8px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 13 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Team Modal in Messages Page */}
      {transferTicketId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div style={{ ...cardStyle, width: 480, maxWidth: '95%' }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: 18 }}>🔄 Move Ticket to Different Team</h3>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              Escalate or re-route ticket <strong>#{transferTicketId.slice(0, 8)}</strong> to another specialized team.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Target Destination Team</label>
              <select
                value={targetTeam}
                onChange={e => setTargetTeam(e.target.value)}
                style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 12px', fontWeight: 700 }}
              >
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d === 'OPERATIONS' ? '⚙️ Operations Team' : d === 'TECH_SUPPORT' || d === 'TECH' ? '💻 Tech Support Team' : d === 'FINANCE' ? '💳 Finance Team' : d === 'SUPPORT' ? '🎧 CS Support Team' : `📁 ${d} Team`}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Assign Agent (Optional - Auto-assigns if left default)</label>
              <select
                value={selectedAgentId}
                onChange={e => setSelectedAgentId(e.target.value)}
                style={{ width: '100%', height: 40, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 12px' }}
              >
                <option value="">⚡ Auto-assign to active online team agent</option>
                {assignableAdmins.filter(a => (a.role && a.role.toUpperCase() === targetTeam.toUpperCase()) || ((a as any).department && (a as any).department.toUpperCase() === targetTeam.toUpperCase()) || a.role === 'ADMIN').map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Transfer Reason / Internal Note</label>
              <textarea
                value={transferNote}
                onChange={e => setTransferNote(e.target.value)}
                placeholder="Explain why this ticket is being moved..."
                style={{ width: '100%', boxSizing: 'border-box', minHeight: 80, padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setTransferTicketId(null)}
                disabled={transferring}
                style={{ border: '1px solid #e2e8f0', background: '#f8fafc', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={handleTransferTeamSubmitInChat}
                disabled={transferring}
                style={{ border: 'none', background: 'linear-gradient(135deg, #c2410c, #ea580c)', color: '#fff', padding: '9px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}
              >
                {transferring ? 'Moving Team…' : 'Confirm Team Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
